import React from 'react'
import _ from 'underscore'
import { Calculator, Check } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import CommitmentDiscountHints from './commitment_discount_hints'
import DiscountLabel from './discount_label'

const SECTION = 'py-4 first:pt-0 last:pb-0'
const SECTION_HEADING = 'mb-3 flex items-center gap-2 text-sm font-semibold'
const RADIO_CLASSES = "size-5 shrink-0 cursor-pointer appearance-none rounded-full border-2 border-border outline-none before:m-[25%] before:block before:size-1/2 before:rounded-full before:content-[''] checked:border-foreground checked:before:bg-foreground hover:border-muted-foreground/40"

class PricingCalculatorForm extends React.Component {

  onNumberOfInstalmentsClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ number_of_instalments: value })
  }

  onLessonsPerWeekClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ lessons_per_week: value })
  }

  onLessonDurationClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ lesson_duration: value })
  }

  onDiscretionaryDiscountClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ discretionary_discount: value })
  }

  onPricingOptionClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ pricing_option: value })
  }

  isSmartStartDealAvailable = () => {
    const inputs = this.props.pricing_calculator.inputs
    return inputs.subject_category_name === 'School Tutoring' && inputs.currency_code === 'ZAR'
  }

  onSmartStartDealClick = () => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    const inputs = this.props.pricing_calculator.inputs

    if (inputs.subject_category_name !== 'School Tutoring') {
      return showMessage('Smart Start Deal is only available for School Tutoring.')
    }

    if (inputs.currency_code !== 'ZAR') {
      return showMessage('Smart Start Deal is only available for ZAR billing.')
    }

    this.props.onUpdate({ pricing_option: 'smart-start-deal', lesson_duration: 45 })
  }

  onTutorCountryClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ tutor_country_code: value })
  }

  onPaymentTermClick = (event) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ payment_term: event.target.value })
  }

  onCommitmentInMonthsClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ commitment_in_months: value })
  }

  onPackageSizeClick = (value) => {
    if (!this.props.lesson_order?.actions?.can_edit) {
      return showMessage('This lesson order has already been paid for.')
    }

    this.props.onUpdate({ package_size: value })
  }

  renderPill(option, active, onClick, muted = false) {
    return (
      <button
        key={option.value}
        type="button"
        onClick={() => onClick(option.value)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
          active ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
          muted && 'cursor-not-allowed opacity-50'
        )}
      >
        <Check className={cn('size-3.5 transition-opacity', active ? 'opacity-100' : 'opacity-0')} />
        {option.name}
      </button>
    )
  }

  renderPillGroup(options, isActive, onClick) {
    return (
      <div className="inline-flex w-fit max-w-full flex-wrap gap-1 rounded-full bg-muted p-1">
        {_.map(options, (option) => this.renderPill(option, isActive(option), onClick))}
      </div>
    )
  }

  // One equal-width grid column per option so the discount hint bands below
  // can span the exact same columns and stay aligned with the month pills.
  renderGridPillGroup(options, isActive, onClick) {
    return (
      <div
        className="grid gap-1 rounded-full bg-muted p-1"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {_.map(options, (option) => {
          const active = isActive(option)
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onClick(option.value)}
              className={cn(
                'flex items-center justify-center gap-1 rounded-full px-1 py-1.5 text-sm font-medium transition-colors',
                active ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {active && <Check className="size-3.5" />}
              {option.name}
            </button>
          )
        })}
      </div>
    )
  }

  renderLessonDurationDiscount() {
    if (this.props.pricing_calculator.discounts.lesson_duration) {
      return (
        <DiscountLabel discount_percent={this.props.pricing_calculator.discounts.lesson_duration} />
      )
    }

    return null
  }

  renderCommitmentDiscount() {
    if (this.props.pricing_calculator.discounts.commitment_in_months) {
      return (
        <DiscountLabel discount_percent={this.props.pricing_calculator.discounts.commitment_in_months} />
      )
    }

    return null
  }

  renderPaymentTermDiscount() {
    if (this.props.pricing_calculator.discounts.payment_term) {
      return (
        <DiscountLabel discount_percent={this.props.pricing_calculator.discounts.payment_term} />
      )
    }

    return null
  }

  render() {
    const inputs = this.props.pricing_calculator.inputs
    const options = this.props.pricing_calculator.options
    const lessonDurationDiscount = this.renderLessonDurationDiscount()
    const commitmentDiscount = this.renderCommitmentDiscount()
    const paymentTermDiscount = this.renderPaymentTermDiscount()
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="size-4"/>
            Pricing Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          <div className={SECTION}>
            <div className={SECTION_HEADING}>
              Payment Terms
              {paymentTermDiscount}
            </div>
            <div className="grid gap-1.5">
              {_.map(options.payment_term, (payment_term) => {
                let checked = inputs.payment_term === payment_term.value

                return (
                  <div className="flex items-center" key={payment_term.value}>
                    <input className={RADIO_CLASSES} type="radio" name={payment_term.name} id={payment_term.value} value={payment_term.value} checked={checked} onChange={this.onPaymentTermClick} />
                    <label className="mb-0 ml-2 cursor-pointer text-sm" htmlFor={payment_term.value}>
                      {payment_term.name}
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
          {(inputs.payment_term === 'eft_instalments') ?
            <div className={SECTION}>
              <div className={SECTION_HEADING}>Number of Instalments</div>
              {this.renderPillGroup(
                options.number_of_instalments,
                (option) => parseInt(inputs.number_of_instalments) === parseInt(option.value),
                this.onNumberOfInstalmentsClick
              )}
            </div>
            : null
          }
          <div className={SECTION}>
            <div className={SECTION_HEADING}>Lessons per week</div>
            {this.renderPillGroup(
              options.lessons_per_week,
              (option) => parseInt(inputs.lessons_per_week) === parseInt(option.value),
              this.onLessonsPerWeekClick
            )}
          </div>
          <div className={SECTION}>
            <div className={SECTION_HEADING}>
              <span>Commitment <small className="text-[13px] font-normal text-muted-foreground">(in months)</small></span>
              {commitmentDiscount}
            </div>
            {this.renderGridPillGroup(
              options.commitment_in_months,
              (option) => parseInt(inputs.commitment_in_months) === parseInt(option.value),
              this.onCommitmentInMonthsClick
            )}
            <CommitmentDiscountHints pricing_calculator={this.props.pricing_calculator} />
          </div>
          {(options.package_size.length > 0) ?
            <div className={SECTION}>
              <div className={SECTION_HEADING}>Package Size</div>
              {this.renderPillGroup(
                options.package_size,
                (option) => parseInt(inputs.package_size) === parseInt(option.value),
                this.onPackageSizeClick
              )}
            </div>
            : null
          }
          <div className={SECTION}>
            <div className={SECTION_HEADING}>
              Lesson duration
              {lessonDurationDiscount}
            </div>
            {this.renderPillGroup(
              options.lesson_duration,
              (option) => inputs.lesson_duration === option.value,
              this.onLessonDurationClick
            )}
          </div>
          {options.tutor_country.length > 0 ?
            <div className={SECTION}>
              <div className={SECTION_HEADING}>Tutor country</div>
              {this.renderPillGroup(
                options.tutor_country,
                (option) => inputs.tutor_country_code === option.value,
                this.onTutorCountryClick
              )}
            </div>
            : null
          }
          <div className={SECTION}>
            <div className={SECTION_HEADING}>Special offer</div>
            <div className="mb-3 text-[13px] text-muted-foreground">Additional discount applied to base hourly rate</div>
            {this.renderPillGroup(
              options.discretionary_discount,
              (option) => parseInt(inputs.discretionary_discount) === parseInt(option.value),
              this.onDiscretionaryDiscountClick
            )}
          </div>
          <div className={SECTION}>
            <div className={SECTION_HEADING}>Pricing option</div>
            <div className="inline-flex w-fit max-w-full flex-wrap gap-1 rounded-full bg-muted p-1">
              {this.renderPill(
                {value: 'standard', name: 'Standard'},
                inputs.pricing_option === 'standard' || inputs.pricing_option === 'normal' || !inputs.pricing_option,
                this.onPricingOptionClick
              )}
              {this.renderPill(
                {value: 'smart-start-deal', name: 'Smart Start Deal'},
                inputs.pricing_option === 'smart-start-deal',
                this.onSmartStartDealClick,
                !this.isSmartStartDealAvailable()
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }
}

export default PricingCalculatorForm
